/**
 * Marcador para tipo que ainda não tem renderer nativo.
 *
 * Existe para que o mapa de renderers seja COMPLETO desde já, e para que a
 * ausência apareça na tela em vez de sumir. O modo antigo de lidar com isso era
 * o `switchDefault` do `ngSwitch`, que desenhava um campo de texto — então um
 * `datagrid` mal configurado virava uma caixinha de texto e ninguém entendia por
 * quê.
 *
 * Em produção o marcador não aparece: `ScreenRendererComponent` só o mostra em
 * `editPreview`. Numa tela publicada, um tipo sem renderer simplesmente não
 * ocupa espaço — errar para menos é melhor que mostrar aviso técnico ao cliente.
 */
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { ScreenNode } from '../model/node-props';
import { TranslateTAPipe } from './translate-ta.pipe';
import { NodeRenderer, RenderMode } from './node-renderer';

@Component({
  selector: 'tem-not-implemented',
  standalone: true,
  imports: [CommonModule, NzIconModule, TranslateTAPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (mode() === 'editPreview') {
      <div class="tem-pending">
        <nz-icon nzType="tool" />
        <span>{{ 'builderScreen.render.notImplemented' | translateTA: { t: typeName() } }}</span>
      </div>
    }
  `,
  styles: [`
    :host { display: block; }

    .tem-pending {
      display: flex;
      align-items: center;
      gap: var(--tem-space-2);
      padding: var(--tem-space-3);
      border: 1px dashed var(--tem-border);
      border-radius: var(--tem-radius);
      background: var(--tem-surface-2);
      color: var(--tem-muted);
      font-size: var(--tem-fs-sm);
    }
  `]
})
export class NotImplementedRenderer implements NodeRenderer {

  readonly node = input.required<ScreenNode>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  readonly typeName = computed<string>(() => this.node().type);
}
